import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HomeService } from 'src/app/home/home-service/home.service';
import { FiltroSelectOption } from '../filtro-select/filtro-select.component';
import { FiltroSelectBuscaComponent } from '../filtro-select-busca/filtro-select-busca.component';

export interface Responsavel {
  id: number;
  name: string;
}

/**
 * Filtro de responsável com busca por nome.
 *
 * Carrega a lista de responsáveis sozinho, então basta declarar o componente:
 *
 *   <app-filtro-responsavel
 *     [value]="filtros.responsibleID"
 *     [incluirTodos]="true"
 *     (valueChange)="onResponsavelChange($event)"
 *   ></app-filtro-responsavel>
 *
 * Use `[responsaveis]` apenas quando a tela precisar de uma lista específica
 * (ex.: apenas responsáveis de um módulo).
 */
@Component({
  selector: 'app-filtro-responsavel',
  standalone: true,
  imports: [CommonModule, FiltroSelectBuscaComponent],
  templateUrl: './filtro-responsavel.component.html',
  styleUrl: './filtro-responsavel.component.scss',
})
export class FiltroResponsavelComBuscaComponent implements OnInit, OnChanges {
  @Input() value: number | null = null;

  /** Adiciona a opção "Todos os responsáveis" (valor 0) no topo da lista. */
  @Input() incluirTodos = false;
  @Input() textoTodos = 'Todos os responsáveis';

  @Input() placeholder = 'Selecionar responsável';
  @Input() placeholderBusca = 'Buscar responsável';
  @Input() disabled = false;

  /** Lista customizada. Quando informada, o componente não busca na API. */
  @Input() responsaveis: Responsavel[] | null = null;

  @Output() valueChange = new EventEmitter<number | null>();
  /** Emitido quando a lista termina de carregar — útil para habilitar ações. */
  @Output() responsaveisCarregados = new EventEmitter<Responsavel[]>();

  lista: Responsavel[] = [];
  carregando = false;

  private readonly _homeService = inject(HomeService);

  ngOnInit(): void {
    if (this.responsaveis) {
      this.lista = this.ordenar(this.responsaveis);
      return;
    }

    this.carregar();
  }

  /** A lista customizada costuma chegar depois (request do pai), então acompanhamos as mudanças. */
  ngOnChanges(changes: SimpleChanges): void {
    const mudanca = changes['responsaveis'];
    if (!mudanca || mudanca.isFirstChange() || !this.responsaveis) return;

    this.lista = this.ordenar(this.responsaveis);
  }

  get opcoes(): FiltroSelectOption[] {
    const opcoes: FiltroSelectOption[] = this.lista.map((r) => ({
      value: r.id,
      label: r.name,
    }));

    return this.incluirTodos
      ? [{ value: 0, label: this.textoTodos }, ...opcoes]
      : opcoes;
  }

  onValueChange(valor: any): void {
    const id = valor === null || valor === undefined ? null : Number(valor);
    this.value = id;
    this.valueChange.emit(id);
  }

  private carregar(): void {
    this.carregando = true;

    this._homeService.listResponsables().subscribe({
      next: (res: any) => {
        this.lista = this.ordenar(
          (res?.data ?? []).map((r: any) => ({ id: Number(r.id), name: r.name })),
        );
        this.carregando = false;
        this.responsaveisCarregados.emit(this.lista);
      },
      error: () => {
        this.lista = [];
        this.carregando = false;
        this.responsaveisCarregados.emit([]);
      },
    });
  }

  private ordenar(lista: Responsavel[]): Responsavel[] {
    return [...lista].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR', { sensitivity: 'base' }),
    );
  }
}
